import store from "@/store";
import { DataModules } from "@/store/modules/data/modules";
import type { Location, Route } from "vue-router";
import { RegexMatch } from "@/data/enums/router";
import { DocCategories } from "@/data/enums/docs";
import type { IModalComponent } from "@/models/modal";
import { FraudStatus } from "@upmind-automation/types";
import { SegmentTypes } from "@/data/enums/segments";
import { InvoiceStatus } from "@/data/enums/invoice";
import {
  ROUTE_QUERY_SEPARATOR,
  ROUTER_OPERATOR_SEPARATOR,
  FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR,
  FILTER_ARRAY_VALUES_SEPARATOR
} from "@/data/table";

import { DefaultOrderSorters } from "@/data/sorters/order";
import OrdersListing from "@/components/app/global/orders/ordersListing.vue";
import SegmentResultsListing from "@/components/app/admin/segments/segmentResultsListing.vue";
import { ListDisplayMode } from "@/data/constants";

// Build a `?OP1=…` config string for the OrdersListing composable
// (default sort `-created_at`, default limit `10`). Used to redirect
// the old route-based quick filters to the composable URL format.
function buildOrdersRedirect(
  field: string,
  operator: string,
  value: string
): Location {
  const config = [
    "1",
    "10",
    "-created_at",
    [field, operator, value].join(ROUTER_OPERATOR_SEPARATOR)
  ].join(ROUTE_QUERY_SEPARATOR);
  return { name: "adminOrders_all", query: { OP1: config } };
}

export default [
  {
    path: "orders",
    components: {
      default: {
        name: "AdminOrders",
        template: `<router-view />`
      }
    },
    meta: {
      breadcrumb: {
        label: "_.orders"
      }
    },
    children: [
      {
        path: "",
        name: "adminOrders",
        component: () => import("@/views/admin/orders/index.vue"),
        props: {
          routes: require("./menu").ordersTabigation
        },
        meta: {
          auth: ["staff_list_invoices", "list_contracts"],
          title: "_.orders"
        },
        redirect: { name: "adminOrdersOverview" },
        children: [
          {
            path: "overview",
            name: "adminOrdersOverview",
            components: {
              default: () => import("@/views/admin/orders/overview/index.vue")
            },
            meta: {
              auth: ["list_contracts"],
              backTo: "_.orders",
              categorySlug: DocCategories.SETUP_CONFIGURATION,
              docsSlugs: ["orders-services"]
            },
            children: [
              {
                path: `:orderId(${RegexMatch.UUID})`,
                name: "adminOrder",
                beforeEnter: async (
                  to: Route,
                  from: Route,
                  next: (
                    options: boolean | Location | Location["path"]
                  ) => Promise<void>
                ) => {
                  const { orderId } = to.params;
                  // Abort routing if no `orderId`
                  if (!orderId) return next(false);
                  // Check for authenticated 'from' route
                  const isAuthenticatedFromRoute = !store.getters[
                    "auth/admin/isLoggedOutPath"
                  ](from.path);
                  // If routed from an authenticated route...
                  if (from.matched.length && isAuthenticatedFromRoute) {
                    // Block routing
                    next(false);
                    // End routing progress
                    store.dispatch("ui/endRouting");
                  } else {
                    // Allow routing to parent location
                    await next({
                      path: to.fullPath.replace(orderId, ""),
                      replace: true
                    });
                  }
                  // Open preview modal
                  const modal: IModalComponent = await store.dispatch(
                    `data/${DataModules.ORDERS}/openPreviewModal`,
                    {
                      props: {
                        orderId,
                        autoCloseOnRouteChange: false
                      },
                      events: {
                        ["route-change"]: ({ from }: { from: Route }) => {
                          const isAuthenticatedFromRoute = !store.getters[
                            "auth/admin/isLoggedOutPath"
                          ](from.path);
                          if (isAuthenticatedFromRoute) modal.close();
                        }
                      }
                    }
                  );
                }
              }
            ]
          },
          {
            path: "list",
            components: {
              default: () => import("@/views/admin/orders/list/index.vue")
            },
            meta: {
              auth: ["list_contracts"]
            },
            children: [
              {
                path: "",
                name: "adminOrdersList",
                redirect: { name: "adminOrders_all" },
                component: {
                  name: "AdminOrdersView",
                  template: "<router-view/>"
                },
                meta: {
                  backTo: "_.orders",
                  categorySlug: DocCategories.SETUP_CONFIGURATION,
                  docsSlugs: ["orders-services"]
                },
                children: [
                  {
                    path: "all",
                    name: "adminOrders_all",
                    component: OrdersListing,
                    props: () => ({
                      availableSorters: DefaultOrderSorters
                    }),
                    meta: {
                      categorySlug: DocCategories.SETUP_CONFIGURATION,
                      docsSlugs: ["orders-services"]
                    }
                  },
                  // Redirect legacy quick-filter routes to the new
                  // composable URL config (preserves existing bookmarks).
                  {
                    path: "paid",
                    name: "adminOrders_paid",
                    redirect: () =>
                      buildOrdersRedirect(
                        "status.code",
                        "eq",
                        InvoiceStatus.PAID
                      )
                  },
                  {
                    path: "unpaid",
                    name: "adminOrders_unpaid",
                    redirect: () =>
                      buildOrdersRedirect(
                        "status.code",
                        "eq",
                        [
                          [InvoiceStatus.UNPAID, InvoiceStatus.ADJUSTED].join(
                            FRONTEND_FILTER_ARRAY_VALUES_SEPARATOR
                          ),
                          InvoiceStatus.OVERDUE
                        ].join(FILTER_ARRAY_VALUES_SEPARATOR)
                      )
                  },
                  {
                    path: "cancelled",
                    name: "adminOrders_cancelled",
                    redirect: () =>
                      buildOrdersRedirect(
                        "status.code",
                        "eq",
                        InvoiceStatus.CANCELLED
                      )
                  },
                  {
                    path: "last_24_hours",
                    name: "adminOrders_last_24_hours",
                    redirect: () =>
                      buildOrdersRedirect("created_at", "after", "-24_hours")
                  },
                  {
                    path: "last_7_days",
                    name: "adminOrders_last_7_days",
                    redirect: () =>
                      buildOrdersRedirect("created_at", "after", "-1_weeks")
                  },
                  {
                    path: "last_30_days",
                    name: "adminOrders_last_30_days",
                    redirect: () =>
                      buildOrdersRedirect("created_at", "after", "-1_months")
                  },
                  {
                    path: "preview",
                    name: "adminOrders_preview",
                    redirect: () =>
                      buildOrdersRedirect(
                        "fraud_status",
                        "eq",
                        String(FraudStatus.REVIEW)
                      )
                  },
                  {
                    path: "blocked",
                    name: "adminOrders_blocked",
                    redirect: () =>
                      buildOrdersRedirect(
                        "fraud_status",
                        "eq",
                        String(FraudStatus.FRAUD)
                      )
                  }
                ]
              },
              {
                path: `segments/:segmentId(${RegexMatch.UUID})`,
                name: "adminOrdersSegments",
                component: {
                  name: "AdminOrdersSegmentList",
                  template: `<router-view  :key="$route.params.segmentId"/>`
                },
                redirect: { name: "adminOrders_segment" },
                meta: {
                  auth: ["list_segments"],
                  backTo: "_.order_segments"
                },
                children: [
                  {
                    path: "",
                    name: "adminOrders_segment",
                    component: SegmentResultsListing,
                    props: (route: Route) => ({
                      segmentId: route.params.segmentId,
                      segmentTypeHint: SegmentTypes.ORDER,
                      newSegmentRouteName: "adminOrdersSegments",
                      defaultDisplayMode: ListDisplayMode.TABLE,
                      displayModes: [ListDisplayMode.TABLE],
                      availableSorters: DefaultOrderSorters
                    })
                  }
                ]
              }
            ]
          }
        ]
      }
    ]
  }
];
